import React, {
  useEffect,
  useState,
  useRef,
  type MutableRefObject,
  type ChangeEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { IoMdArrowBack } from 'react-icons/io';
import { IoIosSearch } from 'react-icons/io';
import { FiCopy, FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { IoAdd, IoTrashOutline } from 'react-icons/io5';

import { fetchVPAs, deleteVPA } from '../../redux/vpaActions';
import { fetchTenant } from '../../redux/tenantActions';
import { fetchProgramWithId } from '../../redux/programActions';
import { useCollectionMode } from '../../hooks/useCollectionMode';
import PlaceholderImage from '../../assests/placeholder_card_img.jpg';
import type { VpaRecord } from '../../types/domain';

type RootState = any;

interface VpaWithMeta extends VpaRecord {
  providerName?: string;
  createdAt?: string;
  qrImage?: string;
}

function VpaPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isWalletAddressCollection: walletMode } = useCollectionMode();
  const { vpa, loading } = useSelector((state: RootState) => state.vpa);
  const { tenant_details } = useSelector((state: RootState) => state.tenant);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setSearchVisible] = useState(false);
  const [filteredVpas, setFilteredVpas] = useState<VpaWithMeta[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVpa, setSelectedVpa] = useState<VpaWithMeta | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<VpaWithMeta | null>(null);
  const [copyStatus, setCopyStatus] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (id) {
      // @ts-expect-error thunk
      dispatch(fetchTenant(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const pid = tenant_details?.programId;
    if (pid != null) {
      // @ts-expect-error thunk
      dispatch(fetchProgramWithId(pid));
    }
  }, [dispatch, tenant_details?.programId]);

  useEffect(() => {
    if (vpa && Array.isArray(vpa) && vpa[0] && 'vpa' in vpa[0]) {
      const list: VpaWithMeta[] = (vpa[0].vpa as VpaWithMeta[])
        .filter(
          (item) =>
            item.vpa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.providerName || '').toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt ?? 0).getTime() -
            new Date(a.createdAt ?? 0).getTime(),
        );
      setFilteredVpas(list);
    } else {
      setFilteredVpas([]);
    }
  }, [vpa, searchTerm]);

  useEffect(() => {
    if (id) {
      // @ts-expect-error thunk
      dispatch(fetchVPAs(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !target?.closest('button[aria-label="Toggle search"]')
      ) {
        setSearchVisible(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleVpaClick = (item: VpaWithMeta) => {
    setSelectedVpa(item);
    setShowDetailModal(true);
    setCopyStatus(false);
  };

  const handleCopyVPA = async () => {
    if (!selectedVpa?.vpa) return;
    try {
      await navigator.clipboard.writeText(selectedVpa.vpa);
      setCopyStatus(true);
      toast.success(walletMode ? 'Copied to clipboard' : 'VPA copied to clipboard');
      setTimeout(() => setCopyStatus(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const confirmDelete = (item: VpaWithMeta) => {
    setDeleteConfirm(item);
  };

  const cancelDelete = () => setDeleteConfirm(null);

  const deleteVpaHandle = async () => {
    if (!deleteConfirm || !id) return;
    const { vpaId } = deleteConfirm;
    try {
      // @ts-expect-error thunk
      await dispatch(deleteVPA(id, vpaId));
      setDeleteConfirm(null);
      toast.success(walletMode ? 'Wallet removed' : 'VPA removed');
    } catch {
      toast.error(walletMode ? 'Failed to delete wallet' : 'Failed to delete VPA');
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const toggleSearchVisibility = () => {
    setSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchTerm('');
    }
  };

  const formatDate = (input: string | undefined) => {
    if (!input) return '';
    const date = new Date(input);
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    const formattedDate = date.toLocaleString('en-US', options).replace(',', '');
    return `Added ${formattedDate}`;
  };

  return (
    <div className="h-screen bg-surface overflow-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {vpa && tenant_details && (
        <div className="h-full flex flex-col">
          <div className="bg-white/95 backdrop-blur border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="h-14 flex items-center">
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/${id}`)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <IoMdArrowBack className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    {!isSearchVisible && (
                      <div className="text-center">
                        <h1 className="text-base font-semibold text-gray-900">
                          {walletMode ? 'Wallets' : 'VPAs'}
                        </h1>
                        <div className="text-xs text-gray-500">{tenant_details.tenantName}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      ref={searchRef as MutableRefObject<HTMLDivElement | null>}
                      className={`overflow-hidden transition-all duration-300 ease-in-out flex items-center ${
                        isSearchVisible ? 'flex-1 max-w-xs' : 'w-0'
                      }`}
                    >
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder={walletMode ? 'Search wallets…' : 'Search VPAs...'}
                        disabled={!isSearchVisible}
                        autoFocus={isSearchVisible}
                        className={`w-full h-10 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all duration-300 bg-gray-50 ${
                          isSearchVisible
                            ? 'opacity-100 translate-x-0 pointer-events-auto'
                            : 'opacity-0 -translate-x-full pointer-events-none'
                        }`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={toggleSearchVisibility}
                      aria-label="Toggle search"
                      className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
                    >
                      <IoIosSearch className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50/80">
            <div className="max-w-5xl mx-auto p-3.5 sm:p-5 pb-24">
              <div className="space-y-6">
                <div className="text-xs text-gray-500">
                  {filteredVpas.length}{' '}
                  {walletMode
                    ? `wallet${filteredVpas.length !== 1 ? 's' : ''}`
                    : `VPA${filteredVpas.length !== 1 ? 's' : ''}`}
                </div>
                {filteredVpas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <IoIosSearch className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium text-sm">
                      {walletMode ? 'No wallets found' : 'No VPAs found'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {searchTerm
                        ? 'Try a different search term'
                        : walletMode
                          ? 'Scan a QR code to add your first wallet address'
                          : 'Scan a QR code to add your first VPA'}
                    </p>
                    {!searchTerm && (
                      <button
                        type="button"
                        onClick={() => navigate(`/tenant/vpa/add/${id}`)}
                        className="mt-4 px-5 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark font-medium text-sm transition-all"
                      >
                        {walletMode ? 'Add wallet' : 'Add VPA'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3.5">
                    {filteredVpas.map((item, index) => (
                      <div
                        key={item.vpaId || index}
                        onClick={() => handleVpaClick(item)}
                        className="bg-white rounded-lg overflow-hidden shadow-soft cursor-pointer transition-all hover:shadow-card-hover border border-gray-100 active:scale-[0.995]"
                      >
                        <div className="p-4 flex items-start gap-4">
                          <div className="w-18 h-18 flex-shrink-0">
                            <img
                              src={item.qrImage || PlaceholderImage}
                              alt="QR Code"
                              className="w-full h-full object-contain bg-gray-50 rounded-lg p-2"
                            />
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="text-[11px] text-gray-500">
                              {formatDate(item.createdAt)}
                            </div>
                            <div className="text-gray-900 font-medium mt-1 break-all text-sm">
                              {item.vpa}
                            </div>
                            {item.providerName && (
                              <div className="text-sm text-gray-600 mt-0.5">
                                {item.providerName}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              confirmDelete(item);
                            }}
                            className="p-2.5 rounded-xl transition-colors flex-shrink-0 text-red-600 hover:bg-red-50"
                          >
                            <IoTrashOutline className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-20">
            <button
              type="button"
              onClick={() => navigate(`/tenant/vpa/add/${id}`)}
              className="w-14 h-14 flex items-center justify-center bg-brand text-white rounded-2xl hover:bg-brand-dark active:scale-95 shadow-soft-lg transition-all"
            >
              <IoAdd className="w-7 h-7" />
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={cancelDelete}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {walletMode ? 'Delete wallet?' : 'Delete VPA?'}
            </h3>
            <p className="text-sm text-gray-600 mb-4 break-all">{deleteConfirm.vpa}</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteVpaHandle}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedVpa && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {walletMode ? 'Wallet details' : 'VPA Details'}
              </h3>
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <MdClose className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 flex justify-center">
                <img
                  src={selectedVpa.qrImage || PlaceholderImage}
                  alt="QR"
                  className="w-48 h-48 object-contain"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {walletMode ? 'Wallet address' : 'VPA Address'}
                </p>
                <p className="text-gray-900 font-medium break-all">{selectedVpa.vpa}</p>
              </div>
              {selectedVpa.providerName && (
                <p className="text-sm text-gray-600">Provider: {selectedVpa.providerName}</p>
              )}
              {selectedVpa.mcc && (
                <p className="text-sm text-gray-600">MCC: {selectedVpa.mcc}</p>
              )}
              <button
                type="button"
                onClick={handleCopyVPA}
                className="w-full flex items-center justify-center gap-2 py-3 bg-brand text-white rounded-xl hover:bg-brand-dark font-medium"
              >
                {copyStatus ? <FiCheck className="w-5 h-5" /> : <FiCopy className="w-5 h-5" />}
                {copyStatus ? 'Copied!' : walletMode ? 'Copy address' : 'Copy VPA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VpaPage;
